import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Bell, Search, User, ChevronDown, Menu, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';

const Header = ({ toggleMobileMenu, toggleSidebar, isSidebarExpanded, isMobileMenuOpen }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { currentUser, userRole, isAdmin, isProvider } = useAuth();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [notifications, setNotifications] = useState([
    { id: 1, title: 'New solar panel deals available', time: '2 hours ago', read: false },
    { id: 2, title: 'Your savings calculation is ready', time: '1 day ago', read: false },
    { id: 3, title: 'Load shedding schedule updated', time: '3 days ago', read: true }
  ]);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/signin');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  return (
    <div className={`
      fixed top-0 right-0 
      ${isMobileMenuOpen ? 'left-64' : 'left-0'} 
      ${isSidebarExpanded ? 'lg:left-64' : 'lg:left-20'}
      transition-all duration-300 ease-in-out
      z-[90]
    `}>
      <header className={`
        h-16 
        ${isDarkMode ? 'bg-blue-800' : 'bg-amber-900'}
        shadow-lg
        flex items-center
        relative
      `}>
        <div className="h-full w-full px-4 flex items-center justify-between">
          {/* Left side */}
          <div className="flex items-center space-x-3">
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-md text-yellow-400 hover:text-yellow-300 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              onClick={toggleMobileMenu}
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>

            {/* Favicon - clickable to home (mobile only) */}
            <button 
              className="lg:hidden text-yellow-400 hover:text-yellow-300 transition-colors duration-150"
              onClick={() => navigate('/')}
              aria-label="Navigate to Home"
            >
              <img 
                src="/favicon.svg" 
                alt="Solar Insight" 
                className="w-8 h-8"
              />
            </button>
          </div>

          {/* Right side items */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            {/* Theme Toggle Button - for mobile only */}
            <button
              onClick={toggleTheme}
              className="lg:hidden p-2 rounded-full text-yellow-400 hover:text-yellow-300 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              aria-label="Toggle theme"
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            <SearchBar 
              isSearchOpen={isSearchOpen} 
              setIsSearchOpen={setIsSearchOpen}
              isDarkMode={isDarkMode}
            />
            
            {/* Show Get Started button for non-logged-in users on desktop */}
            {!currentUser && (
              <Link
                to="/signup"
                className="hidden sm:inline-flex items-center px-4 py-2 bg-yellow-400 text-blue-900 rounded-lg font-medium hover:bg-yellow-300 transition-colors"
              >
                Get Started
              </Link>
            )}
            
            {currentUser && (
              <NotificationBell 
                notifications={notifications}
                setNotifications={setNotifications}
                isNotificationOpen={isNotificationOpen}
                setIsNotificationOpen={setIsNotificationOpen}
                isDarkMode={isDarkMode}
              />
            )}
            <UserMenu 
              isUserMenuOpen={isUserMenuOpen} 
              setIsUserMenuOpen={setIsUserMenuOpen} 
              isDarkMode={isDarkMode} 
              user={currentUser}
              onLogout={handleLogout}
            />
          </div>
        </div>
      </header>
    </div>
  );
};

const SearchBar = ({ isSearchOpen, setIsSearchOpen, isDarkMode }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      setIsSearchOpen(false);
      setSearchTerm('');
    }
  };

  // Handle enter key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      handleSearch(e);
    } else if (e.key === 'Escape') {
      setIsSearchOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div className="relative" ref={searchRef}>
      <button 
        className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-blue-700' : 'hover:bg-amber-700'}`} 
        onClick={() => setIsSearchOpen(!isSearchOpen)}
        aria-label="Search"
      >
        <Search size={20} className="text-white" />
      </button>
      {isSearchOpen && (
        <form onSubmit={handleSearch} className="absolute right-0 top-full mt-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search..."
            className={`
              p-2 rounded
              w-48 sm:w-64 md:w-80
              ${isDarkMode 
                ? 'bg-blue-700 text-white placeholder-blue-300' 
                : 'bg-amber-700 text-white placeholder-amber-300'}
              focus:outline-none focus:ring-2 focus:ring-yellow-400
            `}
            autoFocus
          />
        </form>
      )}
    </div>
  );
};

const NotificationBell = ({ notifications, setNotifications, isNotificationOpen, setIsNotificationOpen, isDarkMode }) => {
  const notificationRef = useRef(null);
  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsNotificationOpen]);

  const markAsRead = (notificationId) => {
    // In a real app, you would update this in the database
    console.log(`Marking notification ${notificationId} as read`);
    if (setNotifications) {
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
    }
  };

  const markAllAsRead = () => {
    // In a real app, you would update all notifications as read in the database
    console.log('Marking all notifications as read');
    if (setNotifications) {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }
  };

  return (
    <div className="relative" ref={notificationRef}>
      <button 
        className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-blue-700' : 'hover:bg-amber-700'} relative`}
        onClick={() => setIsNotificationOpen(!isNotificationOpen)}
      >
        <Bell size={20} className="text-white" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>
      
      {isNotificationOpen && (
        <div className={`
          absolute right-0 mt-2 w-80 rounded-md shadow-lg 
          ${isDarkMode ? 'bg-blue-800 border-blue-700' : 'bg-amber-800 border-amber-700'}
          border z-50 max-h-96 overflow-y-auto custom-scrollbar
        `}>
          <div className={`p-4 border-b ${isDarkMode ? 'border-blue-700' : 'border-amber-700'}`}>
            <div className="flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-300'}`}>
                Notifications
              </h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className={`text-sm ${isDarkMode ? 'text-blue-200 hover:text-blue-100' : 'text-amber-200 hover:text-amber-100'} transition-colors`}
                >
                  Mark all as read
                </button>
              )}
            </div>
          </div>
          
          <div className="py-1">
            {notifications.length === 0 ? (
              <div className={`px-4 py-3 text-center ${isDarkMode ? 'text-blue-200' : 'text-amber-200'}`}>
                No notifications
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-b ${isDarkMode ? 'border-blue-700/30' : 'border-amber-700/30'} cursor-pointer ${
                    isDarkMode ? 'hover:bg-blue-700/50' : 'hover:bg-amber-700/50'
                  } transition-colors ${
                    !notification.read ? (isDarkMode ? 'bg-blue-600/30' : 'bg-amber-600/30') : ''
                  }`}
                  onClick={(e) => {
                    markAsRead(notification.id);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-amber-100'}`}>
                        {notification.title}
                      </p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-blue-200' : 'text-amber-200'}`}>
                        {notification.time}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className={`w-2 h-2 ${isDarkMode ? 'bg-yellow-400' : 'bg-yellow-300'} rounded-full ml-2 mt-1`}></div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const UserMenu = ({ isUserMenuOpen, setIsUserMenuOpen, isDarkMode, user, onLogout }) => {
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { userRole, isAdmin, isProvider } = useAuth();
  const [providerData, setProviderData] = useState(null);
  const [userProfileData, setUserProfileData] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsUserMenuOpen]);

  // Fetch user data based on role
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      
      try {
        if (isProvider) {
          // Fetch provider data
          const providerRef = doc(db, 'providers', user.uid);
          const providerSnap = await getDoc(providerRef);
          if (providerSnap.exists()) {
            setProviderData(providerSnap.data());
          }
        } else {
          // Fetch user profile data for regular users
          const userProfileRef = doc(db, 'userProfiles', user.uid);
          const userProfileSnap = await getDoc(userProfileRef);
          if (userProfileSnap.exists()) {
            setUserProfileData(userProfileSnap.data());
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [isProvider, user]);

  const handleNavigation = (path) => {
    setIsUserMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(path);
  };

  const getDashboardPath = () => {
    if (isAdmin) return '/admin/dashboard';
    if (isProvider) return '/provider/dashboard';
    return '/dashboard';
  };

  const getDashboardLabel = () => {
    if (isAdmin) return 'Admin Dashboard';
    if (isProvider) return 'Provider Dashboard';
    return 'My Dashboard';
  };

  const getUserDisplayText = () => {
    if (!user) return 'Get Started';
    
    // Debug logging
    console.log('User role detection:', { 
      user: user?.email, 
      userRole, 
      isAdmin, 
      isProvider, 
      providerData: providerData?.businessName,
      userProfileData: userProfileData?.displayName 
    });
    
    if (isAdmin) return 'Admin';
    
    if (isProvider && providerData) {
      return providerData.businessName || providerData.tradingName || 'Provider';
    }
    
    if (isProvider) return 'Provider';
    
    // Regular user - try to get name from user profile first, then fallback to Firebase user
    if (userProfileData && userProfileData.displayName) {
      return userProfileData.displayName;
    }
    
    return user.displayName || user.email?.split('@')[0] || 'My Account';
  };

  return (
    <div className="relative" ref={menuRef}>
      <button 
        className={`flex items-center space-x-2 p-2 rounded ${isDarkMode ? 'hover:bg-blue-700' : 'hover:bg-amber-700'}`}
        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
      >
        <User size={20} className="text-white" />
        <span className="text-white hidden sm:inline">
          {getUserDisplayText()}
        </span>
        <ChevronDown size={16} className="text-white" />
      </button>
      {isUserMenuOpen && (
        <div className={`
          absolute right-0 mt-2 w-48 rounded-md shadow-lg 
          ${isDarkMode ? 'bg-blue-700' : 'bg-amber-700'}
          z-50
        `}>
          <div className="py-1">
            {user ? (
              <>
                <button
                  onClick={() => handleNavigation(getDashboardPath())}
                  className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-blue-200 hover:bg-blue-600' : 'text-amber-200 hover:bg-amber-600'}`}
                >
                  {getDashboardLabel()}
                </button>
                
                {/* Show Admin Provider Review for Admins */}
                {isAdmin && (
                  <button
                    onClick={() => handleNavigation('/admin/providers')}
                    className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-blue-200 hover:bg-blue-600' : 'text-amber-200 hover:bg-amber-600'}`}
                  >
                    Provider Review
                  </button>
                )}
                
                <button
                  onClick={() => handleNavigation('/profile')}
                  className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-blue-200 hover:bg-blue-600' : 'text-amber-200 hover:bg-amber-600'}`}
                >
                  Profile
                </button>
                <button
                  onClick={() => handleNavigation('/settings')}
                  className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-blue-200 hover:bg-blue-600' : 'text-amber-200 hover:bg-amber-600'}`}
                >
                  Settings
                </button>
                <button 
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    onLogout();
                  }}
                  className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-blue-200 hover:bg-blue-600' : 'text-amber-200 hover:bg-amber-600'}`}
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <div className={`px-4 py-2 text-xs ${isDarkMode ? 'text-blue-300' : 'text-amber-300'} font-semibold uppercase tracking-wide`}>
                  Welcome to Solar Insight
                </div>
                <div className={`border-t ${isDarkMode ? 'border-blue-600' : 'border-amber-600'} my-1`}></div>
                
                <button
                  onClick={() => handleNavigation('/signin')}
                  className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-blue-200 hover:bg-blue-600' : 'text-amber-200 hover:bg-amber-600'} font-medium`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => handleNavigation('/signup')}
                  className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-blue-200 hover:bg-blue-600' : 'text-amber-200 hover:bg-amber-600'} font-medium`}
                >
                  Create Account
                </button>
                
                <div className={`border-t ${isDarkMode ? 'border-blue-600' : 'border-amber-600'} my-1`}></div>
                
                <button
                  onClick={() => handleNavigation('/provider/onboard')}
                  className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-yellow-300 hover:bg-blue-600' : 'text-yellow-600 hover:bg-amber-600'} font-medium flex items-center`}
                >
                  <span>Become a Provider</span>
                  <span className="ml-2">→</span>
                </button>
                
                <div className={`px-4 py-2 text-xs ${isDarkMode ? 'text-blue-400' : 'text-amber-500'} text-center`}>
                  Join to save your solar system details & calculations
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;